import Box from '@mui/material/Box';
import { styled } from '@mui/material/styles';
import Head from 'next/head';
import Link from 'next/link';

import constMeta from '@/contents/constants/constMeta';
import Button from '@/contents/General/Button';
import Image from '@/contents/General/Image';
import { Board, Thumbnail } from '@/contents/General/Panels';
import Stack from '@/contents/General/Stack';
import Typography from '@/contents/General/Typography';
import Layout from '@/layouts';
import OptimizedImage from '@/modules/General/OptimizedImage';
import theme from '@/styles/theme';
import type { NextPageWithLayout } from '@/types';

const Container = styled(Box)`
  position: relative;
  z-index: 1;
  padding: 0 ${theme.spacing(3)};
`;

const MainVisual = styled(Box)`
  display: flex;
  justify-content: center;
  position: relative;
  width: 100%;
  height: 100vw;
  max-height: 504px;
  border-radius: 8px;
`;

const Index: NextPageWithLayout = () => {
  return (
    <>
      <Head>
        <title>{constMeta.illust.title}</title>
        <meta name="description" content={constMeta.illust.mata_description} />
        <meta property="og:title" content={constMeta.illust.title} />
        <meta
          property="og:description"
          content={constMeta.illust.mata_description}
        />
        <meta
          property="og:image"
          content="https://iihi.life/assets/images/common/ogp.jpg"
        />
      </Head>
      <Container>
        <MainVisual>
          <OptimizedImage
            src="/assets/images/illust/illust_top.png"
            alt="iihiフリーイラストメインビジュアル"
          />
        </MainVisual>
        <Box paddingTop={3} paddingBottom={3}>
          <Typography
            fontSize={28}
            fontWeight={700}
            paddingBottom={3}
            variant="h1"
          >
            iihiフリーイラスト
          </Typography>
          <Typography fontSize={15} paddingBottom={4}>
            iihiでは、キャンプ場やアウトドア施設で活用いただくことを想定した、キャンプのイラスト素材を無償で配布しています。
            <br />
            <br />
            日々の忙しい運営のなかで、少しでもオーナーのみなさんのお役立てとなること、そして初心者の方も含む利用者のみなさんが心地よくアウトドアライフを楽しめることを願って、制作しました。
            <br />
            <br />
            多少のルールは添えていますが、基本的にはご自由にお使いいただけます。ぜひダウンロードのうえご利用ください。
          </Typography>
          <Box paddingBottom={6}>
            <Thumbnail
              image_height={70}
              max_height={300}
              sx={{ '& img': { objectFit: 'contain' } }}
            >
              <OptimizedImage
                src="/assets/images/about/about_character_top.png"
                width="100%"
                height="100%"
                alt="iihiフリーイラスト"
              />
            </Thumbnail>
          </Box>
          <Typography
            fontSize={21}
            fontWeight={700}
            paddingBottom={3}
            variant="h2"
          >
            活用事例
          </Typography>
          <Typography fontSize={15} paddingBottom={4}>
            受付でご案内する場内ルールや、施設内で貼り出すマナー喚起の掲示物など、キャンプ場に合わせて活用いただいています。
          </Typography>
          <Box paddingBottom={6}>
            <Thumbnail
              image_height={100}
              max_height={520}
              sx={{ '& img': { objectFit: 'contain' } }}
            >
              <OptimizedImage
                src="/assets/images/illust/illust_pamphlet.png"
                width="100%"
                height="100%"
                alt="注意事項フライヤー"
              />
            </Thumbnail>
          </Box>
          <Typography
            fontSize={21}
            fontWeight={700}
            paddingBottom={3}
            variant="h2"
          >
            イラスト素材
          </Typography>
          <Typography fontSize={15} paddingBottom={4}>
            キャンプ場内のルールやマナー喚起にあわせたイラストを複数配布しています。キャンプ場や施設のルールに、組み合わせてお使いください。
            <br />
            <br />
            イラストは今後も増やしていく予定ですので、どうぞお楽しみに。
          </Typography>
          <Box paddingBottom={5}>
            <Thumbnail
              image_height={70}
              max_height={300}
              sx={{ '& img': { objectFit: 'contain' } }}
            >
              <Image
                src="/assets/images/illust/illust_list1.png"
                width="100%"
                height="100%"
                alt="イラスト素材(例）"
              />
            </Thumbnail>
          </Box>
          <Stack center paddingBottom={7}>
            <Link href="/illust/list">
              <a>
                <Button
                  buttonSize="md"
                  sx={{ width: '289px', fontSize: '16px', fontWeight: 500 }}
                >
                  <Typography fontSize={16} fontWeight={500} color="white">
                    イラスト素材をすべて見る
                  </Typography>
                </Button>
              </a>
            </Link>
          </Stack>
          <Typography
            fontSize={21}
            fontWeight={700}
            paddingBottom={4}
            variant="h2"
          >
            ご使用にあたって
          </Typography>
          <Board
            no_border="true"
            sx={{
              mb: theme.spacing(7),
              pt: theme.spacing(4),
              pb: theme.spacing(4),
            }}
          >
            <Typography
              fontSize={16}
              fontWeight={700}
              color="red"
              lineHeight={1.5}
              paddingBottom={2}
            >
              イラスト素材は、キャンプ場内の掲示物や、SNSでのバナー画像など、ご自由にお使いいただけます。
            </Typography>
            <Typography fontSize={15} paddingBottom={4}>
              ただし、以下のような内容・目的ではご利用いただけませんのでご注意ください。
              <br />
              <br />
              ・イラスト素材を使用した商品開発、販売
              <br />
              ・イラスト素材を改変した使用
              <br />
              ・アウトドア、キャンプ領域以外での使用
              <br />
              <br />
              イラスト素材の使用に関するルールの詳細は、よくあるご質問ページとダウンロードページで表示される利用規約とあわせてご確認ください。
            </Typography>
            <Stack center>
              <Link href={`/faq/category/faqcategory-12/`}>
                <a>
                  <Button
                    buttonSize="md"
                    sx={{ width: '289px', fontSize: '16px', fontWeight: 500 }}
                  >
                    よくあるご質問を見る
                  </Button>
                </a>
              </Link>
            </Stack>
          </Board>
          {/* <Box paddingBottom={5}>
            <Thumbnail image_height={42} max_height={214}>
              <Image
                src="https://placehold.co/380x214"
                width="100%"
                height="100%"
                alt="キャラクターイラスト"
              />
            </Thumbnail>
          </Box> */}
          <Stack center paddingBottom={7}>
            <Link href="/illust/download">
              <a>
                <Button
                  buttonSize="md"
                  color="secondary"
                  sx={{
                    width: '289px',
                    fontWeight: 500,
                    fontSize: '16px',
                  }}
                >
                  イラスト素材ダウンロード
                </Button>
              </a>
            </Link>
          </Stack>
        </Box>
      </Container>
    </>
  );
};

Index.getLayout = (page) => <Layout>{page}</Layout>;

export default Index;
